import { useState } from 'react'
import dayjs from 'dayjs'

export const useWeightTracking = (entry, userDisplayName, userPhotoURL, updateEntryMutation) => {
  const [displayUnit, setDisplayUnit] = useState('lbs')
  const [chartTimePeriod, setChartTimePeriod] = useState('all')
  const [newWeight, setNewWeight] = useState('')
  const [weightDate, setWeightDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [weightUnit, setWeightUnit] = useState('lbs')

  // Weight conversion utilities
  const convertToGrams = (weight, unit) => {
    switch (unit) {
      case 'lbs': return weight * 453.592
      case 'oz': return weight * 28.3495
      case 'g': return weight
      default: return weight
    }
  }

  const convertFromGrams = (grams, unit) => {
    switch (unit) {
      case 'lbs': return grams / 453.592
      case 'oz': return grams / 28.3495
      case 'g': return grams
      default: return grams
    }
  }

  const formatWeight = (grams, unit) => {
    const converted = convertFromGrams(grams, unit)
    return unit === 'g' ? Math.round(converted) : converted.toFixed(2)
  }

  const getUnitLabel = (unit) => {
    switch (unit) {
      case 'lbs': return 'lbs'
      case 'oz': return 'oz'
      case 'g': return 'g'
      default: return 'lbs'
    }
  }

  const handleAddWeight = async (entryId, onClose) => {
    if (!newWeight || !weightDate) return

    try {
      // Convert input weight to grams for storage (standardized)
      const weightInGrams = convertToGrams(parseFloat(newWeight), weightUnit)
      
      const weightEntry = {
        weightGrams: weightInGrams,
        originalWeight: parseFloat(newWeight),
        originalUnit: weightUnit,
        date: weightDate,
        timestamp: new Date(),
        recordedBy: userDisplayName || 'Unknown'
      }

      const currentWeights = entry?.weights || []
      const updatedWeights = [...currentWeights, weightEntry].sort((a, b) => new Date(b.date) - new Date(a.date))

      await updateEntryMutation.mutateAsync({
        entryId,
        entryData: {
          ...entry,
          weights: updatedWeights
        },
        userDisplayName,
        userPhotoURL
      })

      setNewWeight('')
      setWeightDate(dayjs().format('YYYY-MM-DD'))
      onClose()
    } catch (error) {
      console.error('Error adding weight:', error)
    }
  }

  const handleEditWeight = async (entryId, weightIndex, updatedWeightData, onClose) => {
    try {
      const currentWeights = [...(entry?.weights || [])]
      
      // Convert input weight to grams for storage
      const weightInGrams = convertToGrams(parseFloat(updatedWeightData.weight), updatedWeightData.unit)
      
      // Update the weight at the specified index
      currentWeights[weightIndex] = {
        ...currentWeights[weightIndex],
        weightGrams: weightInGrams,
        originalWeight: parseFloat(updatedWeightData.weight),
        originalUnit: updatedWeightData.unit,
        date: updatedWeightData.date,
        // Keep original timestamp and recordedBy, but update modified info
        modifiedBy: userDisplayName || 'Unknown',
        modifiedAt: new Date()
      }

      // Sort weights by date (newest first)
      const updatedWeights = currentWeights.sort((a, b) => new Date(b.date) - new Date(a.date))

      await updateEntryMutation.mutateAsync({
        entryId,
        entryData: {
          ...entry,
          weights: updatedWeights
        },
        userDisplayName,
        userPhotoURL
      })

      onClose && onClose()
    } catch (error) {
      console.error('Error editing weight:', error)
    }
  }

  const handleDeleteWeight = async (entryId, weightIndex) => {
    try {
      const currentWeights = [...(entry?.weights || [])]
      
      // Remove the weight at the specified index
      currentWeights.splice(weightIndex, 1)

      await updateEntryMutation.mutateAsync({
        entryId,
        entryData: {
          ...entry,
          weights: currentWeights
        },
        userDisplayName,
        userPhotoURL
      })
    } catch (error) {
      console.error('Error deleting weight:', error)
    }
  }

  // Chart data preparation
  const getFilteredWeights = () => {
    if (!entry?.weights) return []
    
    const now = dayjs()
    let filterDate = dayjs()
    
    switch (chartTimePeriod) {
      case '1m': filterDate = now.subtract(1, 'month'); break
      case '3m': filterDate = now.subtract(3, 'month'); break
      case '6m': filterDate = now.subtract(6, 'month'); break
      case '1y': filterDate = now.subtract(1, 'year'); break
      default: filterDate = dayjs(0) // 'all' - show everything
    }
    
    return entry.weights
      .filter(weight => dayjs(weight.date).isAfter(filterDate) || dayjs(weight.date).isSame(filterDate, 'day'))
      .sort((a, b) => dayjs(a.date).unix() - dayjs(b.date).unix())
  }

  const getChartData = () => {
    const weights = getFilteredWeights()
    return weights.map((weight, index) => ({
      date: dayjs(weight.date).format('M/D'),
      weight: parseFloat(formatWeight(weight.weightGrams || weight.weight * 453.592, displayUnit)),
      originalDate: weight.date,
      change: index > 0 ? 
        parseFloat(formatWeight(weight.weightGrams || weight.weight * 453.592, displayUnit)) - 
        parseFloat(formatWeight(weights[index - 1].weightGrams || weights[index - 1].weight * 453.592, displayUnit))
        : 0
    }))
  }

  const calculateGrowthRate = () => {
    const weights = getFilteredWeights()
    if (weights.length < 2) return null
    
    const firstWeight = weights[0]
    const lastWeight = weights[weights.length - 1]
    const firstDate = dayjs(firstWeight.date)
    const lastDate = dayjs(lastWeight.date)
    const daysDiff = lastDate.diff(firstDate, 'day')
    
    if (daysDiff === 0) return null
    
    const firstWeightConverted = formatWeight(firstWeight.weightGrams || firstWeight.weight * 453.592, displayUnit)
    const lastWeightConverted = formatWeight(lastWeight.weightGrams || lastWeight.weight * 453.592, displayUnit)
    const weightChange = lastWeightConverted - firstWeightConverted
    
    return {
      totalChange: weightChange,
      dailyRate: (weightChange / daysDiff),
      weeklyRate: (weightChange / daysDiff) * 7,
      monthlyRate: (weightChange / daysDiff) * 30,
      period: daysDiff
    }
  }

  return {
    // State
    displayUnit,
    setDisplayUnit,
    chartTimePeriod,
    setChartTimePeriod,
    newWeight,
    setNewWeight,
    weightDate,
    setWeightDate,
    weightUnit,
    setWeightUnit,
    
    // Functions
    formatWeight,
    getUnitLabel,
    handleAddWeight,
    handleEditWeight,
    handleDeleteWeight,
    getFilteredWeights,
    getChartData,
    calculateGrowthRate
  }
}